import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  TextField,
} from "@mui/material";
import { ChevronDown, Plus } from "lucide-react";
import useIsDarkTheme from "../../lib/hooks/useIsDarkTheme";
import { getLocalStorage, setLocalStorage, LOCAL_STORAGE_KEYS } from "../../lib/utils/localStorageUtil";
import { ocppFeaturesApi } from "./hooks/useOcppFeaturesApi";

export default function FeatureFilterSelect({ options, value, onChange, onFeaturesAdded }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [customText, setCustomText] = useState("");
  const [customFeatures, setCustomFeatures] = useState([]);
  const isDark = useIsDarkTheme();
  const open = Boolean(anchorEl);
  const selectedValues = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  useEffect(() => {
    const saved = getLocalStorage(LOCAL_STORAGE_KEYS.CUSTOM_FEATURE_OPTIONS) || [];
    if (Array.isArray(saved) && saved.length) setCustomFeatures(saved);
  }, []);

  const persistCustomFeatures = useCallback(updated => {
    setCustomFeatures(updated);
    setLocalStorage(LOCAL_STORAGE_KEYS.CUSTOM_FEATURE_OPTIONS, updated);
  }, []);

  const allOptions = useMemo(() => {
    const baseSet = new Set(options);
    const extras = customFeatures.filter(f => !baseSet.has(f));
    return [...options, ...extras];
  }, [options, customFeatures]);

  const orderedOptions = useMemo(() => {
    const selected = [];
    const unselected = [];

    allOptions.forEach(option => {
      if (selectedSet.has(option)) {
        selected.push(option);
      } else {
        unselected.push(option);
      }
    });

    return [...selected, ...unselected];
  }, [allOptions, selectedSet]);

  const canAddCustom = useMemo(() => {
    const text = customText.trim();
    if (!text) return false;
    return !allOptions.some(opt => opt.toLowerCase() === text.toLowerCase());
  }, [customText, allOptions]);

  const handleToggle = option => {
    if (selectedSet.has(option)) {
      onChange(selectedValues.filter(item => item !== option));
      return;
    }

    onChange([...selectedValues, option]);
  };

  const handleAddCustom = () => {
    const text = customText.trim();
    if (!text || !canAddCustom) return;
    persistCustomFeatures([...customFeatures, text]);
    onChange([...selectedValues, text]);
    setCustomText("");

    ocppFeaturesApi
      .addFeatures([text])
      .then(() => {
        onFeaturesAdded?.([text]);
      })
      .catch(err => {
        console.warn("Failed to persist custom feature to backend:", err);
      });
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        fullWidth
        onClick={event => setAnchorEl(event.currentTarget)}
        endIcon={
          <ChevronDown
            size={16}
            style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}
          />
        }
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          borderRadius: "8px",
          backgroundColor: isDark ? "#111827" : "#fff",
          color: selectedValues.length > 0 ? "var(--color-accent-active)" : isDark ? "#e5e7eb" : "#374151",
          borderColor:
            open || selectedValues.length > 0 ? "var(--color-accent-active)" : isDark ? "#374151" : "#e5e7eb",
          fontSize: "0.8rem",
          fontWeight: 500,
          minHeight: 36,
          px: 1,
          py: 0.55,
          "&:hover": {
            borderColor: "var(--color-accent-active)",
            backgroundColor: isDark ? "var(--color-accent-alpha-08)" : "var(--color-accent-lightest)",
          },
          "& .MuiButton-endIcon": {
            marginLeft: 1,
            color: "inherit",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            fontSize: "0.8rem",
          }}
        >
          {selectedValues.length === 0
            ? "Feature"
            : `${selectedValues.length} feature${selectedValues.length > 1 ? "s" : ""} selected`}
        </Box>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            maxWidth: 320,
            maxHeight: 350,
            borderRadius: "10px",
            border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
            background: isDark ? "#1f2937" : "#fff",
            boxShadow: isDark ? "0 12px 36px rgba(0, 0, 0, 0.35)" : "0 12px 36px rgba(15, 23, 42, 0.12)",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <List
          dense
          sx={{
            py: 0.5,
            flex: 1,
            overflowY: "auto",
            maxHeight: 320,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDark ? "#4b5563" : "#d1d5db",
              borderRadius: 4,
            },
          }}
        >
          {orderedOptions.map(option => {
            const isSelected = selectedSet.has(option);

            return (
              <ListItem key={option} disablePadding>
                <ListItemButton
                  onClick={() => handleToggle(option)}
                  sx={{
                    alignItems: "center",
                    px: 0.75,
                    py: 0.45,
                    gap: 0.25,
                    backgroundColor: isSelected
                      ? isDark
                        ? "var(--color-accent-alpha-08)"
                        : "var(--color-accent-lightest)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: isDark ? "var(--color-accent-alpha-08)" : "var(--color-accent-lightest)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 26, m: 0 }}>
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        p: 0.35,
                        color: isDark ? "#6b7280" : "#d1d5db",
                        "&.Mui-checked": { color: "var(--color-accent-active)" },
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    sx={{ my: 0, minWidth: 0 }}
                    primary={option}
                    primaryTypographyProps={{
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      color: isSelected ? (isDark ? "#f9fafb" : "#1f2937") : isDark ? "#d1d5db" : "#374151",
                      fontWeight: isSelected ? 600 : 400,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Box
          sx={{
            px: 0.75,
            py: 0.75,
            borderTop: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
            flexShrink: 0,
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="Add custom feature..."
            value={customText}
            onChange={e => setCustomText(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") handleAddCustom();
              e.stopPropagation();
            }}
            InputProps={{
              endAdornment: canAddCustom ? (
                <InputAdornment position="end" sx={{ cursor: "pointer" }} onClick={handleAddCustom}>
                  <Plus size={16} color="var(--color-accent-active)" />
                </InputAdornment>
              ) : null,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: isDark ? "#111827" : "#f9fafb",
                fontSize: "0.8rem",
                height: 32,
                color: isDark ? "#e5e7eb" : "#1f2937",
                "& fieldset": { borderColor: isDark ? "#374151" : "#e5e7eb" },
                "&:hover fieldset": { borderColor: isDark ? "#4b5563" : "#d1d5db" },
                "&.Mui-focused fieldset": { borderColor: "var(--color-accent-active)", borderWidth: "1.5px" },
              },
              "& .MuiInputBase-input": {
                color: isDark ? "#e5e7eb" : "#1f2937",
                "&::placeholder": { color: isDark ? "#6b7280" : "#9ca3af", opacity: 1 },
              },
            }}
          />
        </Box>
      </Popover>
    </>
  );
}

FeatureFilterSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onFeaturesAdded: PropTypes.func,
};
